// timelapseShare — タイムラプス X 共有 (T3) の pure ヘルパ群
// (growth-timelapse-share-2026-07-02.md §3 経路 C / §5.2 編集1画面 / §5.3 T3)。
//
// 共有経路 (§3 確定):
//   - Web Share API (files) が使える環境 (主にスマホ) → `navigator.share` で
//     OS 共有シート → X アプリに動画添付。
//   - 使えない環境 (主に PC) → 「動画を保存」(DL) + 「X で投稿画面を開く」
//     (`https://x.com/intent/post?text=...`) の 2 ステップにフォールバック。
//
// プリフィル文面は「ハッシュタグ + トップ URL」。**部屋 URL は入れない**
// (非公開部屋の URL = capability であり、公開ポストに載ると漏洩する。§3 確定)。
//
// このモジュールは DOM / fetch / worker に触れない pure 関数のみ (= vitest node
// 環境で全分岐テスト可能)。navigator は narrow な shape (NavigatorShareLike) で
// 引数注入する (= SnapshotLoader の FetchFn と同じ流儀)。型のみ
// infrastructure/timelapse から借りる (= application → infrastructure の型 import
// は DispatcherClient 各 form ヘルパと同じ既存精神)。

import type { StrokeLogEvent } from '@oekaki/protocol';
import type { TimelapseMode } from '../infrastructure/timelapse/framePlan.js';
import { HARD_MAX_OUTPUT_SECONDS } from '../infrastructure/timelapse/framePlan.js';
import type { GenerateTimelapseVideoOutcome } from '../infrastructure/timelapse/generateTimelapseVideo.js';
import { collectFinalDocumentRanges } from '../infrastructure/timelapse/lineageSeed.js';
import type { SeqRange } from '../infrastructure/timelapse/segmentMerge.js';
import {
  assertSeqRangeList,
  trimEventsToRanges,
} from '../infrastructure/timelapse/segmentMerge.js';
import { formatCaptureTimestamp } from './uiFeedback.js';

/** 共有プリフィルに載せる導線 URL (= トップ。部屋 URL は載せない、§3 確定)。 */
export const OEKAKI_TOP_URL = 'https://www.takamin.com';

/** ハッシュタグタイムラインが「見せる場」(§2) — 文言を変えるときはここだけ。 */
export const TIMELAPSE_SHARE_HASHTAG = '#タカミンお絵描きチャット';

/** 共有プリフィル本文 (Web Share の text / X intent の text 共通)。 */
export const TIMELAPSE_SHARE_TEXT = `${TIMELAPSE_SHARE_HASHTAG} で描いた\n${OEKAKI_TOP_URL}`;

/**
 * X の Web Intent (post) URL を組み立てる (§3 経路 A = C の PC フォールバック)。
 * メディア添付はできないので「動画を保存 → 投稿画面に手で添付」の 2 ステップ前提。
 */
export const buildXIntentPostUrl = (text: string = TIMELAPSE_SHARE_TEXT): string =>
  `https://x.com/intent/post?text=${encodeURIComponent(text)}`;

const pad2 = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

/**
 * トリムスライダーのラベル用日時 (`6/28 21:04` 形式)。イベントの server-stamp
 * `ts` (epoch ms) をユーザの**ローカル時刻**で出す (= formatCaptureTimestamp と
 * 同じくロケール非依存の手組み。テストが node 環境で決定論的に走る)。
 */
export const formatTimelapseTimeLabel = (epochMs: number): string => {
  const d = new Date(epochMs);
  return `${d.getMonth() + 1}/${d.getDate()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/** 範囲ラベル (`6/28 21:04 〜 7/3 19:12`)。§5.2「プレビューは作らない」の代替表示。 */
export const formatTimelapseRangeLabel = (fromMs: number, toMs: number): string =>
  `${formatTimelapseTimeLabel(fromMs)} 〜 ${formatTimelapseTimeLabel(toMs)}`;

/** DL ファイル名 (= キャプチャ PNG `oekaki-YYYYMMDD-HHmmss.png` と同じ流儀)。 */
export const timelapseFileName = (date: Date): string =>
  `oekaki-log-${formatCaptureTimestamp(date)}.mp4`;

/** 共有経路 (§3 経路 C の 2 分岐)。 */
export type TimelapseSharePath = 'web-share' | 'download-intent';

/**
 * `navigator` の共有関連 subset。実行環境の Navigator をそのまま渡せるよう
 * メソッドは optional (Web Share API 未実装環境では undefined)。
 */
export interface NavigatorShareLike {
  readonly canShare?: (data: { files: File[] }) => boolean;
  readonly share?: (data: { files: File[]; text?: string }) => Promise<void>;
}

/**
 * 共有経路の分岐 (§3): `navigator.canShare({files})` が真なら Web Share、
 * それ以外 (API 無し / files 非対応 / canShare が投げる古い実装) は DL + intent。
 */
export const decideTimelapseSharePath = (
  nav: NavigatorShareLike | undefined,
  file: File,
): TimelapseSharePath => {
  if (nav?.canShare === undefined || nav.share === undefined) return 'download-intent';
  try {
    return nav.canShare({ files: [file] }) ? 'web-share' : 'download-intent';
  } catch {
    return 'download-intent';
  }
};

// ── 長さ/倍速の UI モデル (§5.2) ────────────────────────────────────────────

/** 長さプリセット (§5.2: 15/30/60 秒、既定 30)。 */
export const TIMELAPSE_DURATION_PRESETS = [15, 30, 60] as const;
export const DEFAULT_TIMELAPSE_DURATION_SECONDS = 30;

/** 倍率の選択肢 (§5.2 副モード「×2/×8/×32 等」)。 */
export const TIMELAPSE_MULTIPLIER_OPTIONS = [2, 4, 8, 16, 32] as const;
export const DEFAULT_TIMELAPSE_MULTIPLIER = 8;

/**
 * カスタム秒数の clamp: `1 〜 maxSeconds`。`maxSeconds` は §5.5 の品質プリセット
 * × プラットフォームで解決した上限 (`resolveTimelapseQuality(...).maxSeconds`) を
 * フロントが渡す。省略時は絶対ハード上限 {@link HARD_MAX_OUTPUT_SECONDS} に倒す
 * (= 予算解決を通さない旧来呼び出しの後方互換)。
 */
export const clampTimelapseCustomSeconds = (
  raw: number,
  maxSeconds: number = HARD_MAX_OUTPUT_SECONDS,
): number => {
  const upper = Number.isFinite(maxSeconds)
    ? Math.max(1, Math.floor(maxSeconds))
    : HARD_MAX_OUTPUT_SECONDS;
  if (!Number.isFinite(raw)) return Math.min(DEFAULT_TIMELAPSE_DURATION_SECONDS, upper);
  return Math.min(upper, Math.max(1, Math.round(raw)));
};

/**
 * 編集1画面の「長さ」選択 state (= UI がそのまま持つ形)。既定は
 * duration 30 秒 (= 開いたら無編集で「作成」を押せる、§5.2 原則)。
 */
export interface TimelapseLengthSelection {
  readonly lengthMode: 'duration' | 'multiplier';
  /** duration モード: プリセット秒数 or 'custom' (customSeconds を使う)。 */
  readonly durationChoice: number | 'custom';
  /** カスタム秒数入力の生文字列 (= input value。確定時に clamp)。 */
  readonly customSeconds: string;
  /** multiplier モード: ×N。 */
  readonly multiplierX: number;
}

export const DEFAULT_TIMELAPSE_LENGTH_SELECTION: TimelapseLengthSelection = {
  lengthMode: 'duration',
  durationChoice: DEFAULT_TIMELAPSE_DURATION_SECONDS,
  customSeconds: `${DEFAULT_TIMELAPSE_DURATION_SECONDS}`,
  multiplierX: DEFAULT_TIMELAPSE_MULTIPLIER,
};

/**
 * UI の長さ選択 → 生成リクエストの `TimelapseMode` (T2 `generateTimelapseVideo`)。
 * `maxSeconds` (§5.5、品質プリセット × プラットフォームの解決上限) を渡すと
 * カスタム秒数がそこで clamp される。省略時はハード上限 (後方互換)。
 */
export const resolveTimelapseMode = (
  sel: TimelapseLengthSelection,
  maxSeconds: number = HARD_MAX_OUTPUT_SECONDS,
): TimelapseMode => {
  if (sel.lengthMode === 'multiplier') return { kind: 'multiplier', x: sel.multiplierX };
  if (sel.durationChoice !== 'custom') {
    return {
      kind: 'duration',
      seconds: clampTimelapseCustomSeconds(sel.durationChoice, maxSeconds),
    };
  }
  return {
    kind: 'duration',
    seconds: clampTimelapseCustomSeconds(Number(sel.customSeconds), maxSeconds),
  };
};

// ── 系譜連結ドメイン (§4.7、past-canvas-new-document-2026-07-05.md) ─────────
//
// 共有モーダルのスライダーが操作する**仮想タイムライン**。restore は可動域の
// 「壁」ではなく「接続」— 可動域 = 系譜チェーン (root→leaf) の範囲を連結した
// 仮想列で、飛ばされた中間文書・各文書の非復元 tail・restore マーカー自体は
// 列に現れない。純粋な全消し境界のクランプ (v0.2.2-beta1) は「先頭範囲の下端 =
// 最終文書の開始」として自然に維持される。

/**
 * スライダードメイン: `ranges` は系譜連結の範囲リスト
 * (`collectFinalDocumentRanges` 由来、昇順・互いに素)、`events` はその範囲に
 * 含まれるイベントのみを連結した**仮想列** (seq 昇順)。スライダー index は
 * この仮想列上で動き、範囲ラベル (ts) / プレビュー / 生成 request の材料も
 * すべてこの列で一貫させる。
 */
export interface TimelapseSliderDomain {
  readonly ranges: readonly SeqRange[];
  readonly events: readonly StrokeLogEvent[];
}

/**
 * 系譜連結ドメインを構築する (§4.7)。
 *
 *   - `endSeq` 省略 (通常 = 現在キャンバスからの絵ログ共有): 終端 = 最新
 *     イベント。ドメイン = 今画面に残っている最終文書の系譜。
 *   - `endSeq` 指定 (過去キャンバス一覧から開いた場合 = その `snapshotSeq`):
 *     終端をその seq にした上で、**その過去キャンバスの系譜**がドメインに
 *     なる (従来の「その文書のみ」から系譜込みに広がる、§4.7 どおり)。
 *
 * 「続きを描く」直後 (restore が最終イベント、続き未描画) でもドメインは
 * 復元元の系譜になり**空にならない** (v0.2.2-beta1 クランプ + restore 境界の
 * 合成で空になった報告バグの修正点)。`endSeq` が全イベントより前の病的
 * ケースは最新終端に倒す (= 空ドメインを作らない、旧 `timelapseEndIndexForSeq`
 * と同じ倒し方)。イベントが 1 件も無ければ空ドメイン (呼び出し側が empty 扱い)。
 */
export const buildTimelapseSliderDomain = (
  events: readonly StrokeLogEvent[],
  endSeq?: number,
): TimelapseSliderDomain => {
  const lastSeq = events[events.length - 1]?.seq;
  if (lastSeq === undefined) return { ranges: [], events: [] };
  const build = (toSeq: number): TimelapseSliderDomain => {
    const ranges = collectFinalDocumentRanges(events, toSeq);
    return { ranges, events: trimEventsToRanges(events, ranges) };
  };
  const domain = build(endSeq ?? lastSeq);
  if (domain.events.length > 0 || endSeq === undefined) return domain;
  return build(lastSeq);
};

/**
 * トリムスライダー (仮想列 index 2 ハンドル) → 生成リクエストの**範囲リスト**
 * (inclusive、昇順・互いに素)。選択区間 `[events[startIdx].seq ..
 * events[endIdx].seq]` をドメインの各範囲と交差させるので、選択が範囲間
 * ジャンプ (= restore の接続) を跨ぐ場合は自動的に複数範囲に分割される。
 * 全選択ならドメインの範囲リストそのもの。両端 index はドメイン内のイベント
 * を指すため結果は必ず非空 (`assertSeqRangeList` で不変条件を固定)。
 */
export const buildTimelapseSeqRanges = (
  domain: TimelapseSliderDomain,
  startIdx: number,
  endIdx: number,
): readonly SeqRange[] => {
  const first = domain.events[startIdx];
  const last = domain.events[endIdx];
  if (first === undefined || last === undefined || startIdx > endIdx) {
    throw new TypeError(`buildTimelapseSeqRanges: invalid index range ${startIdx}..${endIdx}`);
  }
  const out: SeqRange[] = [];
  for (const r of domain.ranges) {
    const fromSeq = Math.max(r.fromSeq, first.seq);
    const toSeq = Math.min(r.toSeq, last.seq);
    if (fromSeq <= toSeq) out.push({ fromSeq, toSeq });
  }
  assertSeqRangeList(out, 'buildTimelapseSeqRanges');
  return out;
};

/**
 * 「最後のドキュメント境界の次」の index (修正2、§5.2「全消し以降を動画対象に」)。
 *
 * 範囲内の**最後のドキュメント境界**の**次**のイベントを指す。境界は:
 *   - 全消し (`whiteboard.clear` かつ `layerId === undefined` = 全レイヤ wipe。
 *     判定基準は replay / cropBox と統一)
 *   - 過去キャンバス復元 (`whiteboard.restore`、Slice 3 §4.5-2)
 *
 * 境界が無ければ 0 (= 全体)。境界が最終イベントで以降に何も無い場合 (start が
 * 範囲外) は 0 に倒す (= 開始 > 終了を作らない)。
 *
 * **develop 注意 (§4.7 系譜連結ドメイン)**: 共有モーダルのスライダー材料は
 * `buildTimelapseSliderDomain` の仮想列になり、境界 (restore / 系譜外の全消し)
 * は仮想列に現れないため、本ヘルパは modal では使われなくなった。main
 * (系譜連結ドメイン非搭載) 用の {@link trimToCurrentDocument} の境界検出
 * としてのみ温存。
 */
export const defaultTimelapseStartIndex = (events: readonly StrokeLogEvent[]): number => {
  let start = 0;
  for (let i = 0; i < events.length; i++) {
    const ev = events[i];
    if (
      ev !== undefined &&
      ((ev.t === 'whiteboard.clear' && ev.layerId === undefined) || ev.t === 'whiteboard.restore')
    ) {
      start = i + 1;
    }
  }
  // 境界が最終イベント (以降に描画なし) → 全体に倒す (start > endIdx を作らない)。
  return start > events.length - 1 ? 0 : start;
};

/**
 * 共有 UI に見せるイベント列を「最後の境界 (全消し / 復元) 以降 (= 現在の作品)」
 * に切り詰める (hotfix 2026-07-11、v0.2.2-beta1)。
 *
 * 絵ログは room 単位で全消しをまたいで連続保存されているが、「全消し = 消えた」
 * と理解しているユーザーがスライダーを左に戻すと過去の絵が再出現して混乱する
 * ため、共有 UI のスライダー可動域そのものを現在の文書に限定する。全消し以前の
 * 絵の共有は第2弾の過去キャンバス一覧が正式な入口になる (境界マーカー付き
 * 自由ドラッグ = consult 2026-07-11 ②案もそちらで再検討)。
 *
 * 境界検出は `defaultTimelapseStartIndex` をそのまま流用する: 境界が無ければ
 * そのまま、境界が最終イベント (以降に描画なし) の edge は同関数が 0 に倒す
 * ので従来どおり全体を返す (= 空列を作らない)。
 *
 * **develop 注意 (§4.7 系譜連結ドメイン)**: develop の modal は
 * `buildTimelapseSliderDomain` の仮想列を可動域にする — restore は境界では
 * なく「接続」(復元元の系譜が可動域に入る) なので、restore も境界として切る
 * 本ヘルパの意味論とは §4.7 決定により**意図的に異なる**。main (系譜連結
 * ドメイン非搭載、restore 自体も無い) 用の配列切り詰め実装として温存。
 */
export const trimToCurrentDocument = (
  events: readonly StrokeLogEvent[],
): readonly StrokeLogEvent[] => {
  const boundary = defaultTimelapseStartIndex(events);
  return boundary > 0 ? events.slice(boundary) : events;
};

// ── 警告 / 失敗文言 (§5.2 + T1/T2 申し送り) ────────────────────────────────

/** empty-log の文言 (T0 デプロイ以前の部屋は正当に空、§5.3.1)。 */
export const TIMELAPSE_EMPTY_LOG_MESSAGE = 'この部屋にはまだ絵ログがありません';

/**
 * 生成 summary からユーザ向け警告文を導出する (T1/T2 申し送り、
 * vandalism-recovery-ui-2026-09-27.md「タイムラプスの imagepaste 再生」節で
 * 再署名対応後は件数付きに変更):
 *   - `skippedImagepastes > 0` → 貼り付け画像の一部/全部が replay されない
 *     (再署名 失敗 / fetch 失敗 / decode 失敗)。全件失敗のときも
 *     `totalImagepastes` 件中 `skippedImagepastes` 件、と件数を出す (0 件中
 *     0 件にはならない — この分岐自体 `skippedImagepastes > 0` が前提なので
 *     `totalImagepastes >= skippedImagepastes > 0`)。
 *   - `gaps` / `skippedSegments` → ログ欠落区間を跨いだ (PUT 失敗 / crash /
 *     セグメント破損。replay は続行するが動画に映らない区間がある)。
 */
export const timelapseWarnings = (summary: {
  readonly totalImagepastes: number;
  readonly skippedImagepastes: number;
  readonly gaps: readonly unknown[];
  readonly skippedSegments: readonly unknown[];
}): readonly string[] => {
  const warnings: string[] = [];
  if (summary.skippedImagepastes > 0) {
    warnings.push(
      `${summary.totalImagepastes} 件中 ${summary.skippedImagepastes} 件の貼り付け画像を再生できませんでした`,
    );
  }
  if (summary.gaps.length > 0 || summary.skippedSegments.length > 0) {
    warnings.push('絵ログの一部が欠けているため、動画に含まれない区間があります');
  }
  return warnings;
};

/**
 * 生成失敗 outcome → ユーザ向け文言。`ok` / `cancelled` / `empty-log` は失敗
 * ではないので呼び出し側が先に分岐する (来たらここでは汎用文言に落とす)。
 */
export const describeTimelapseShareFailure = (
  outcome: GenerateTimelapseVideoOutcome['outcome'],
): string => {
  switch (outcome) {
    case 'not-found':
      return 'この部屋の絵ログが見つかりませんでした';
    case 'unavailable':
      return '絵ログサーバが利用できません。時間をおいて再度お試しください';
    case 'load-error':
      return '絵ログの取得に失敗しました。通信環境を確認して再度お試しください';
    case 'unsupported':
      return 'この環境では動画を作成できません (H.264 エンコード非対応)';
    case 'encode-error':
    case 'worker-error':
      return '動画の作成に失敗しました。再度お試しください';
    default:
      return '動画の作成に失敗しました';
  }
};
